import React, { useMemo, useRef, useState } from 'react';
import {
  Plus, Download, Loader2, CheckCircle2, XCircle, FileText, Square, Trash2,
  OctagonX, ArrowUp, ArrowDown, X, MessageSquare, GitBranch, PlayCircle,
} from 'lucide-react';
import {
  createProcessMapSession,
  submitRevisionBatch,
  addRevisionChange,
  updateRevisionChanges,
  applyRevisionDocument,
  applyReadyRevisions,
  downloadProcessMapDocument,
  stopProcessMapDocument,
} from './processMapSynthesisApi';
import { ProcessMapDocument, ProcessMapDocumentStatus, ProcessMapSession, RevisionChange, RevisionSourceKind } from './types';

const MAX_FILES = 6;
const SOP_ACCEPT = '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const EXTRA_ACCEPT = '.txt,.md,.docx,.pdf,.csv,.xlsx,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const SOURCE_KINDS: Array<{ id: RevisionSourceKind; label: string; description: string }> = [
  { id: 'redline', label: 'Redlines', description: 'Tracked changes in the returned Word file' },
  { id: 'comments', label: 'Comments', description: 'Review comments in the margin' },
  { id: 'other', label: 'Other', description: 'Notes or extra files that describe the edits' },
];

const STATUS_LABEL: Record<string, string> = {
  queued: 'Queued',
  parsing: 'Reading changes',
  review: 'Review changes',
  applying: 'Applying edits',
  complete: 'Complete',
  failed: 'Failed',
  stopped: 'Stopped',
};

const STATUS_SORT_ORDER: Record<string, number> = {
  queued: 0,
  parsing: 1,
  applying: 2,
  review: 3,
  stopped: 4,
  failed: 5,
  complete: 6,
};

const TERMINAL: ProcessMapDocumentStatus[] = ['complete', 'failed', 'stopped', 'review'];

type SortField = 'fileName' | 'status' | 'createdAt';
type SortDir = 'asc' | 'desc';

interface StagingItem {
  id: string;
  file: File | null;
  sourceKind: RevisionSourceKind;
  extras: File[];
}

function requestError(err: any, fallback: string): string {
  const server = err?.response?.data?.error;
  if (typeof server === 'string' && server.trim()) return server;
  return fallback;
}

function isTerminal(status: ProcessMapDocumentStatus): boolean {
  return TERMINAL.includes(status);
}

function formatCreatedAt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function revisedDownloadName(fileName: string): string {
  const base = (fileName || 'SOP').replace(/\.[^.]+$/, '').replace(/\s*\(Revised\)$/i, '');
  return `${base} (Revised).docx`;
}

function newStagingItem(): StagingItem {
  return { id: `item_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, file: null, sourceKind: 'redline', extras: [] };
}

function StatusBadge({ doc }: { doc: ProcessMapDocument }) {
  if (doc.status === 'complete') {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 text-xs font-medium">
        <CheckCircle2 className="w-3.5 h-3.5" /> Complete
      </span>
    );
  }
  if (doc.status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 text-xs font-medium" title={doc.error || ''}>
        <XCircle className="w-3.5 h-3.5" /> Failed
      </span>
    );
  }
  if (doc.status === 'stopped') {
    return (
      <span className="inline-flex items-center gap-1 text-slate-700 bg-slate-50 border border-slate-300 rounded-full px-2 py-0.5 text-xs font-medium">
        <OctagonX className="w-3.5 h-3.5" /> Stopped
      </span>
    );
  }
  if (doc.status === 'review') {
    return (
      <span className="inline-flex items-center gap-1 text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-xs font-medium">
        Review changes
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 text-xs font-medium">
      <Loader2 className="w-3.5 h-3.5 animate-spin" /> {STATUS_LABEL[doc.status] || doc.status}
    </span>
  );
}

interface ProcessMapRevisionsPanelProps {
  session: ProcessMapSession | null;
  restoring?: boolean;
  onSession: (session: ProcessMapSession) => void;
  onSessionChange?: (id: number | null) => void;
  onError: (message: string | null) => void;
  onDelete: (doc: ProcessMapDocument) => void;
  ensurePollingIfNeeded: (session: ProcessMapSession) => void;
}

export default function ProcessMapRevisionsPanel({
  session,
  restoring,
  onSession,
  onSessionChange,
  onError,
  onDelete,
  ensurePollingIfNeeded,
}: ProcessMapRevisionsPanelProps) {
  const [staging, setStaging] = useState<StagingItem[]>([newStagingItem()]);
  const [uploading, setUploading] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [openNotesId, setOpenNotesId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [drafts, setDrafts] = useState<Record<string, { original_snippet: string; proposed_content: string; reasoning: string }>>({});
  const sopInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const extraInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const documents = useMemo(
    () => (session?.documents || []).filter((d) => d.kind === 'revision'),
    [session],
  );

  const sortedDocuments = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...documents].sort((a, b) => {
      if (sortField === 'fileName') return dir * (a.fileName || '').localeCompare(b.fileName || '', undefined, { sensitivity: 'base' });
      if (sortField === 'status') return dir * ((STATUS_SORT_ORDER[a.status] ?? 99) - (STATUS_SORT_ORDER[b.status] ?? 99));
      const aTime = a.completedAt || a.startedAt ? new Date((a.completedAt || a.startedAt) as string).getTime() : 0;
      const bTime = b.completedAt || b.startedAt ? new Date((b.completedAt || b.startedAt) as string).getTime() : 0;
      return dir * (aTime - bTime);
    });
  }, [documents, sortField, sortDir]);

  const readyToSubmit = staging.filter((item) => item.file).length > 0 && !uploading;
  const reviewDocs = documents.filter((d) => d.status === 'review' && (d.detectedChanges || []).some((c) => c.approved));

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortDir(field === 'createdAt' ? 'desc' : 'asc');
    }
  };

  const updateStaging = (id: string, patch: Partial<StagingItem>) => {
    setStaging((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const handleUpdate = async () => {
    const items = staging.filter((item) => item.file).map((item) => ({
      file: item.file as File,
      sourceKind: item.sourceKind,
      extras: item.sourceKind === 'other' ? item.extras : [],
    }));
    if (!items.length) {
      onError('Upload at least one returned SOP.');
      return;
    }
    setUploading(true);
    onError(null);
    try {
      let sess = session;
      if (!sess) {
        sess = await createProcessMapSession('Process Map Synthesis');
        onSession(sess);
        onSessionChange?.(sess.id);
      }
      const { session: updated } = await submitRevisionBatch(sess.id, items);
      onSession(updated);
      setStaging([newStagingItem()]);
      ensurePollingIfNeeded(updated);
    } catch (err: any) {
      onError(requestError(err, 'Failed to start the revision'));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: ProcessMapDocument) => {
    if (!session) return;
    try {
      await downloadProcessMapDocument(session.id, doc.id, revisedDownloadName(doc.fileName));
    } catch (err: any) {
      onError(requestError(err, 'Download failed'));
    }
  };

  const handleStop = async (doc: ProcessMapDocument) => {
    if (!session) return;
    setPendingAction(`stop:${doc.id}`);
    onError(null);
    try {
      const updated = await stopProcessMapDocument(session.id, doc.id);
      onSession(updated);
      ensurePollingIfNeeded(updated);
    } catch (err: any) {
      onError(requestError(err, 'Stop failed'));
    } finally {
      setPendingAction(null);
    }
  };

  const persistApprovals = async (doc: ProcessMapDocument, approvals: Record<string, boolean>) => {
    if (!session) return;
    try {
      const updated = await updateRevisionChanges(session.id, doc.id, { approvals });
      onSession(updated);
    } catch (err: any) {
      onError(requestError(err, 'Could not save that approval.'));
    }
  };

  const persistEdit = async (doc: ProcessMapDocument, changeId: string, proposed_content: string) => {
    if (!session) return;
    try {
      const updated = await updateRevisionChanges(session.id, doc.id, { edits: { [changeId]: { proposed_content } } });
      onSession(updated);
    } catch (err: any) {
      onError(requestError(err, 'Could not save that edit.'));
    }
  };

  const handleAddChange = async (doc: ProcessMapDocument) => {
    if (!session) return;
    const draft = drafts[doc.id];
    if (!draft || !draft.original_snippet.trim() || !draft.proposed_content.trim()) {
      onError('Add the current text and the replacement.');
      return;
    }
    setPendingAction(`add:${doc.id}`);
    onError(null);
    try {
      const updated = await addRevisionChange(session.id, doc.id, draft);
      onSession(updated);
      setDrafts((prev) => ({ ...prev, [doc.id]: { original_snippet: '', proposed_content: '', reasoning: '' } }));
      setOpenNotesId(doc.id);
    } catch (err: any) {
      onError(requestError(err, 'Could not add that change.'));
    } finally {
      setPendingAction(null);
    }
  };

  const handleApply = async (doc: ProcessMapDocument) => {
    if (!session) return;
    setPendingAction(`apply:${doc.id}`);
    onError(null);
    try {
      const updated = await applyRevisionDocument(session.id, doc.id);
      onSession(updated);
      ensurePollingIfNeeded(updated);
    } catch (err: any) {
      onError(requestError(err, 'Apply failed'));
    } finally {
      setPendingAction(null);
    }
  };

  const handleApplyReady = async () => {
    if (!session) return;
    setPendingAction('apply-ready');
    onError(null);
    try {
      const updated = await applyReadyRevisions(session.id);
      onSession(updated);
      ensurePollingIfNeeded(updated);
    } catch (err: any) {
      onError(requestError(err, 'Apply failed'));
    } finally {
      setPendingAction(null);
    }
  };

  const SortHeader = ({ field, label, align = 'left' }: { field: SortField; label: string; align?: 'left' | 'right' }) => {
    const active = sortField === field;
    return (
      <th className={`${align === 'right' ? 'text-right' : 'text-left'} px-4 py-2 font-medium`}>
        <button
          type="button"
          onClick={() => handleSort(field)}
          className={`inline-flex items-center gap-0.5 uppercase tracking-wide hover:text-gray-800 ${active ? 'text-gray-800' : ''}`}
        >
          {label}
          {active ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <span className="w-3 h-3" />}
        </button>
      </th>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="border border-gray-200 rounded-lg p-4 bg-white space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Returned SOPs</h3>
            <p className="text-sm text-gray-500 mt-1">
              Upload the file the client sent back. Choose how the edits arrived. Other exposes extra notes files. Then Update to propose the edits for review.
            </p>
          </div>
          {staging.length < MAX_FILES && (
            <button
              type="button"
              onClick={() => setStaging((prev) => [...prev, newStagingItem()])}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-electric-blue border border-electric-blue/30 rounded-md hover:bg-blue-50"
            >
              <Plus className="w-4 h-4" /> Add SOP
            </button>
          )}
        </div>

        <div className="space-y-3">
          {staging.map((item, index) => (
            <div key={item.id} className="border border-gray-200 rounded-md p-3 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">SOP {index + 1} of {MAX_FILES}</div>
                {staging.length > 1 && (
                  <button type="button" onClick={() => setStaging((prev) => prev.filter((row) => row.id !== item.id))} className="text-xs text-red-600 hover:underline">
                    Remove
                  </button>
                )}
              </div>
              <div>
                <input
                  ref={(el) => { sopInputRefs.current[item.id] = el; }}
                  type="file"
                  accept={SOP_ACCEPT}
                  className="hidden"
                  onChange={(e) => updateStaging(item.id, { file: e.target.files?.[0] || null })}
                />
                <button
                  type="button"
                  onClick={() => sopInputRefs.current[item.id]?.click()}
                  className="w-full border border-dashed border-gray-300 rounded-md px-3 py-2 text-sm text-left hover:border-gray-400"
                >
                  {item.file ? (
                    <span className="inline-flex items-center gap-2 text-gray-800">
                      <FileText className="w-4 h-4 text-gray-400" /> {item.file.name}
                    </span>
                  ) : (
                    <span className="text-gray-500">Upload the returned Word SOP (.docx)</span>
                  )}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {SOURCE_KINDS.map((kind) => {
                  const selected = item.sourceKind === kind.id;
                  return (
                    <button
                      key={kind.id}
                      type="button"
                      onClick={() => updateStaging(item.id, { sourceKind: kind.id, extras: kind.id === 'other' ? item.extras : [] })}
                      className={`text-left p-3 rounded-md border ${selected ? 'border-electric-blue bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                    >
                      <div className="font-medium text-sm text-gray-900">{kind.label}</div>
                      <div className="text-xs text-gray-600 mt-0.5">{kind.description}</div>
                    </button>
                  );
                })}
              </div>
              {item.sourceKind === 'other' && (
                <div>
                  <input
                    ref={(el) => { extraInputRefs.current[item.id] = el; }}
                    type="file"
                    accept={EXTRA_ACCEPT}
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const next = Array.from(e.target.files || []);
                      updateStaging(item.id, { extras: [...item.extras, ...next] });
                      if (extraInputRefs.current[item.id]) extraInputRefs.current[item.id]!.value = '';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => extraInputRefs.current[item.id]?.click()}
                    className="text-sm font-medium text-electric-blue hover:underline"
                  >
                    Add additional documents
                  </button>
                  <p className="text-xs text-gray-500 mt-1">.txt, .md, .docx, .pdf, .csv, .xlsx. Not video or audio.</p>
                  {item.extras.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {item.extras.map((file, extraIndex) => (
                        <li key={`${file.name}-${extraIndex}`} className="flex items-center justify-between text-sm text-gray-700">
                          <span className="truncate">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => updateStaging(item.id, { extras: item.extras.filter((_, i) => i !== extraIndex) })}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            disabled={!readyToSubmit}
            onClick={handleUpdate}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-electric-blue rounded-md disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Update
          </button>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Documents</span>
          {reviewDocs.length > 1 && (
            <button
              type="button"
              disabled={pendingAction === 'apply-ready'}
              onClick={handleApplyReady}
              className="text-xs font-medium text-electric-blue hover:underline disabled:opacity-50"
            >
              {pendingAction === 'apply-ready' ? 'Applying…' : `Apply approved (${reviewDocs.length})`}
            </button>
          )}
        </div>
        {documents.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-400 text-center">
            {restoring ? 'Loading this session…' : 'No revisions yet — upload a returned SOP and click Update.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-gray-500 text-xs uppercase tracking-wide">
              <tr>
                <SortHeader field="fileName" label="Document" />
                <SortHeader field="status" label="Status" />
                <SortHeader field="createdAt" label="Final SOP" align="right" />
                <th className="text-right px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedDocuments.map((doc) => {
                const terminal = isTerminal(doc.status);
                const stopBusy = pendingAction === `stop:${doc.id}`;
                const applyBusy = pendingAction === `apply:${doc.id}`;
                const createdLabel = formatCreatedAt(doc.completedAt || doc.startedAt);
                const notes = doc.reviewNotes || [];
                const changes = doc.detectedChanges || [];
                const approvedCount = changes.filter((c) => c.approved).length;
                const draft = drafts[doc.id] || { original_snippet: '', proposed_content: '', reasoning: '' };
                return (
                  <React.Fragment key={doc.id}>
                    <tr>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="text-gray-800 truncate">{doc.fileName}</span>
                        </div>
                        {doc.sourceKind && (
                          <div className="text-xs text-gray-500 ml-6 mt-0.5 capitalize">{doc.sourceKind === 'redline' ? 'Redlines' : doc.sourceKind}</div>
                        )}
                        {doc.status === 'failed' && doc.error && (
                          <div className="text-xs text-red-600 mt-0.5 ml-6">{doc.error}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <StatusBadge doc={doc} />
                        {doc.status === 'review' && (
                          <div className="text-xs text-gray-500 mt-1">{approvedCount} of {changes.length} approved</div>
                        )}
                        {(notes.length > 0 || changes.length > 0 || doc.status === 'review') && (
                          <button type="button" onClick={() => setOpenNotesId(openNotesId === doc.id ? null : doc.id)} className="block text-xs font-medium text-electric-blue mt-1 hover:underline">
                            {openNotesId === doc.id ? 'Hide notes' : 'Notes'}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {doc.status === 'complete' ? (
                          <div className="inline-flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleDownload(doc)}
                              className="inline-flex items-center gap-1 text-electric-blue hover:underline text-sm font-medium"
                            >
                              <Download className="w-3.5 h-3.5" /> {revisedDownloadName(doc.fileName)}
                            </button>
                            {createdLabel && <span className="text-xs text-gray-500 whitespace-nowrap">{createdLabel}</span>}
                          </div>
                        ) : (
                          <span className="text-gray-300 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-1">
                          {doc.status === 'review' && (
                            <button
                              type="button"
                              onClick={() => handleApply(doc)}
                              disabled={applyBusy || approvedCount === 0}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-electric-blue rounded hover:opacity-90 disabled:opacity-50"
                            >
                              {applyBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                              Apply
                            </button>
                          )}
                          {!terminal && (
                            <button
                              type="button"
                              onClick={() => handleStop(doc)}
                              disabled={stopBusy}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
                            >
                              {stopBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
                              Stop
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => onDelete(doc)}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-white border border-red-200 rounded hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                    {openNotesId === doc.id && (
                      <tr>
                        <td colSpan={4} className="px-4 pb-3 bg-gray-50">
                          {notes.length > 0 && (
                            <div className="grid gap-2 md:grid-cols-2 mb-3">
                              {notes.map((note, i) => (
                                <div key={i} className="bg-white border border-gray-200 rounded-md p-3">
                                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium border ${note.tone === 'needs-person' ? 'text-amber-700 bg-amber-50 border-amber-200' : note.tone === 'fixed' || note.tone === 'ok' ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-gray-600 bg-gray-50 border-gray-200'}`}>
                                    {note.tone === 'needs-person' ? 'Needs a person' : note.tone === 'fixed' ? 'Fixed in the Word file' : 'Note'}
                                  </span>
                                  <div className="text-sm font-medium text-gray-900 mt-2">{note.title}</div>
                                  <p className="text-sm text-gray-600 mt-1">{note.body}</p>
                                </div>
                              ))}
                            </div>
                          )}
                          {changes.length > 0 && (
                            <div className="space-y-2">
                              {changes.map((change) => (
                                <ChangeCard
                                  key={change.id}
                                  change={change}
                                  disabled={doc.status !== 'review'}
                                  onToggle={() => persistApprovals(doc, { [change.id]: !change.approved })}
                                  onProposedBlur={(value) => persistEdit(doc, change.id, value)}
                                />
                              ))}
                            </div>
                          )}
                          {doc.status === 'review' && (
                            <div className="mt-3 bg-white border border-gray-200 rounded-md p-3 space-y-2">
                              <div className="text-sm font-medium text-gray-900">Add a missed change</div>
                              <textarea
                                value={draft.original_snippet}
                                onChange={(e) => setDrafts((prev) => ({ ...prev, [doc.id]: { ...draft, original_snippet: e.target.value } }))}
                                placeholder="Current text in the SOP"
                                className="w-full text-sm border border-gray-200 rounded-md p-2"
                                rows={2}
                              />
                              <textarea
                                value={draft.proposed_content}
                                onChange={(e) => setDrafts((prev) => ({ ...prev, [doc.id]: { ...draft, proposed_content: e.target.value } }))}
                                placeholder="Replacement text"
                                className="w-full text-sm border border-gray-200 rounded-md p-2"
                                rows={2}
                              />
                              <input
                                value={draft.reasoning}
                                onChange={(e) => setDrafts((prev) => ({ ...prev, [doc.id]: { ...draft, reasoning: e.target.value } }))}
                                placeholder="Why (optional)"
                                className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5"
                              />
                              <button
                                type="button"
                                onClick={() => handleAddChange(doc)}
                                disabled={pendingAction === `add:${doc.id}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-super-dark-purple rounded-md disabled:opacity-50"
                              >
                                {pendingAction === `add:${doc.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                                Add change
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ChangeCard({
  change,
  disabled,
  onToggle,
  onProposedBlur,
}: {
  change: RevisionChange;
  disabled: boolean;
  onToggle: () => void;
  onProposedBlur: (value: string) => void;
}) {
  const [proposed, setProposed] = useState(change.proposed_content || '');
  const skipped = change.match_method === 'skipped' || change.status === 'skipped';
  const required = !skipped && (change.locked || change.match_method === 'glossary' || change.source_signal === 'glossary');
  return (
    <div className={`bg-white border rounded-md p-3 ${change.approved ? 'border-green-300' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase text-gray-600">{change.change_type}</span>
            {change.source_signal === 'tracked_change' && <GitBranch className="w-3.5 h-3.5 text-gray-400" />}
            {change.source_signal === 'comment' && <MessageSquare className="w-3.5 h-3.5 text-gray-400" />}
            {required && <span className="text-xs font-medium text-emerald-700">Required</span>}
            {change.added_by_user && <span className="text-xs text-electric-blue">Added in review</span>}
            {skipped && <span className="text-xs text-gray-500">Skipped</span>}
          </div>
          {change.reasoning && <p className="text-xs text-gray-500 mt-1">{change.reasoning}</p>}
        </div>
        <button
          type="button"
          disabled={disabled || skipped || required}
          onClick={onToggle}
          className={`px-3 py-1 rounded-md text-xs font-medium ${
            required
              ? 'bg-emerald-700 text-white'
              : skipped || disabled
                ? 'bg-gray-100 text-gray-400'
                : change.approved
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-700'
          }`}
        >
          {required ? 'Required' : skipped ? 'Not applicable' : change.approved ? 'Approved' : 'Approve'}
        </button>
      </div>
      {change.original_snippet && (
        <div className="mt-2 bg-red-50 border border-red-200 rounded p-2 text-sm text-gray-700 whitespace-pre-wrap">{change.original_snippet}</div>
      )}
      <textarea
        value={proposed}
        disabled={disabled || required}
        onChange={(e) => setProposed(e.target.value)}
        onBlur={() => {
          if (proposed !== (change.proposed_content || '')) onProposedBlur(proposed);
        }}
        className="mt-2 w-full bg-green-50 border border-green-200 rounded p-2 text-sm text-gray-700"
        rows={3}
      />
    </div>
  );
}
